import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getCentralCostCenterCodes,
    getAmendIndents,
    getIndentCategories,
    checkIndentQty,
    getAmendIndentItems,
    checkIndentPOStatus,
    autoCompleteIndentItem,
    savePOIndentItem,
    deleteNewAmendItem,
    saveIndentItemAmend,
    cancelIndent,
} from '../../api/PurchaseAPI/indentAmendAPI';

// ── Async Thunks ─────────────────────────────────────────────────────────────

export const fetchCCList = createAsyncThunk(
    'indentAmend/fetchCCList',
    async ({ uid, rid }, { rejectWithValue }) => {
        try {
            const res = await getCentralCostCenterCodes(uid, rid);
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load cost centers');
        }
    }
);

export const fetchAmendIndentList = createAsyncThunk(
    'indentAmend/fetchAmendIndentList',
    async (ccCode, { rejectWithValue }) => {
        try {
            const res = await getAmendIndents(ccCode);
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load indents');
        }
    }
);

export const fetchIndentCategories = createAsyncThunk(
    'indentAmend/fetchIndentCategories',
    async (cType, { rejectWithValue }) => {
        try {
            const res = await getIndentCategories(cType);
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load item categories');
        }
    }
);

export const checkAmendIndentQty = createAsyncThunk(
    'indentAmend/checkAmendIndentQty',
    async ({ indentNo, ccCode }, { rejectWithValue }) => {
        try {
            const res = await checkIndentQty(indentNo, ccCode);
            return res.data?.Data;
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to check indent quantity');
        }
    }
);

/** Loads the amendable item grid + PO status together (mirrors the legacy IndentAmendGrid action) */
export const fetchAmendIndentItems = createAsyncThunk(
    'indentAmend/fetchAmendIndentItems',
    async ({ ccCode, indentNo }, { rejectWithValue }) => {
        try {
            const [itemsRes, statusRes] = await Promise.all([
                getAmendIndentItems(ccCode, indentNo),
                checkIndentPOStatus(indentNo),
            ]);
            return {
                items: itemsRes.data?.Data || [],
                poStatus: statusRes.data?.Data || '',
            };
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load indent items');
        }
    }
);

export const fetchItemSuggestions = createAsyncThunk(
    'indentAmend/fetchItemSuggestions',
    async ({ prefix, type, category }, { rejectWithValue }) => {
        try {
            const res = await autoCompleteIndentItem(prefix, type, category);
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load suggestions');
        }
    }
);

export const addIndentItem = createAsyncThunk(
    'indentAmend/addIndentItem',
    async ({ itemCode, costCenter, indentNo }, { rejectWithValue }) => {
        try {
            const res = await savePOIndentItem({ itemCode, costCenter, indentNo });
            const data = res.data?.Data;
            if (data?.NewItemStatus !== 'Submited') {
                return rejectWithValue(data?.NewItemStatus || 'Failed to add item');
            }
            return data.NewItemData;
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to add item');
        }
    }
);

export const removeIndentItem = createAsyncThunk(
    'indentAmend/removeIndentItem',
    async ({ itemcode, indentListId, indentNo }, { rejectWithValue }) => {
        try {
            const res = await deleteNewAmendItem({ itemcode, indentListId, indentNo });
            const status = res.data?.Data;
            if (status !== 'Submited') {
                return rejectWithValue(status || 'Failed to remove item');
            }
            return indentListId;
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to remove item');
        }
    }
);

export const submitIndentAmend = createAsyncThunk(
    'indentAmend/submit',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await saveIndentItemAmend(payload);
            const status = res.data?.Data;
            if (status !== 'Submited') {
                return rejectWithValue(status || 'Failed to save indent amendment');
            }
            return status;
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to save indent amendment');
        }
    }
);

export const closeIndent = createAsyncThunk(
    'indentAmend/closeIndent',
    async ({ indentNo, createdBy, roleId }, { rejectWithValue }) => {
        try {
            const res = await cancelIndent(indentNo, createdBy, roleId);
            const status = res.data?.Data;
            if (status !== 'Closed') {
                return rejectWithValue(status || 'Failed to close indent');
            }
            return status;
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to close indent');
        }
    }
);

// ── Slice ────────────────────────────────────────────────────────────────────

const initialState = {
    ccList: [],
    indentList: [],
    categoryList: [],
    qtyCheckResult: null,
    items: [],
    poStatus: '',
    suggestions: [],
    saveResult: null,
    closeResult: null,
    loading: {
        ccList: false,
        indentList: false,
        categoryList: false,
        qtyCheck: false,
        items: false,
        suggestions: false,
        addItem: false,
        removeItem: false,
        save: false,
        close: false,
    },
    errors: {
        ccList: null,
        indentList: null,
        categoryList: null,
        items: null,
        addItem: null,
        removeItem: null,
        save: null,
        close: null,
    },
};

const indentAmendSlice = createSlice({
    name: 'indentAmend',
    initialState,
    reducers: {
        clearIndentList:     (s) => { s.indentList = []; },
        clearAmendItems:     (s) => { s.items = []; s.poStatus = ''; s.errors.items = null; },
        clearItemSuggestions:(s) => { s.suggestions = []; },
        clearQtyCheckResult: (s) => { s.qtyCheckResult = null; },
        clearSaveResult:     (s) => { s.saveResult = null; s.errors.save = null; },
        clearCloseResult:    (s) => { s.closeResult = null; s.errors.close = null; },
        resetIndentAmend:    () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCCList.pending,   (s) => { s.loading.ccList = true; s.errors.ccList = null; })
            .addCase(fetchCCList.fulfilled, (s, a) => { s.loading.ccList = false; s.ccList = a.payload; })
            .addCase(fetchCCList.rejected,  (s, a) => { s.loading.ccList = false; s.errors.ccList = a.payload; });

        builder
            .addCase(fetchAmendIndentList.pending,   (s) => { s.loading.indentList = true; s.errors.indentList = null; })
            .addCase(fetchAmendIndentList.fulfilled, (s, a) => { s.loading.indentList = false; s.indentList = a.payload; })
            .addCase(fetchAmendIndentList.rejected,  (s, a) => { s.loading.indentList = false; s.errors.indentList = a.payload; });

        builder
            .addCase(fetchIndentCategories.pending,   (s) => { s.loading.categoryList = true; s.errors.categoryList = null; })
            .addCase(fetchIndentCategories.fulfilled, (s, a) => { s.loading.categoryList = false; s.categoryList = a.payload; })
            .addCase(fetchIndentCategories.rejected,  (s, a) => { s.loading.categoryList = false; s.errors.categoryList = a.payload; });

        builder
            .addCase(checkAmendIndentQty.pending,   (s) => { s.loading.qtyCheck = true; s.qtyCheckResult = null; })
            .addCase(checkAmendIndentQty.fulfilled, (s, a) => { s.loading.qtyCheck = false; s.qtyCheckResult = a.payload; })
            .addCase(checkAmendIndentQty.rejected,  (s) => { s.loading.qtyCheck = false; });

        builder
            .addCase(fetchAmendIndentItems.pending, (s) => {
                s.loading.items = true; s.errors.items = null; s.items = []; s.poStatus = '';
            })
            .addCase(fetchAmendIndentItems.fulfilled, (s, a) => {
                s.loading.items = false; s.items = a.payload.items; s.poStatus = a.payload.poStatus;
            })
            .addCase(fetchAmendIndentItems.rejected, (s, a) => { s.loading.items = false; s.errors.items = a.payload; });

        builder
            .addCase(fetchItemSuggestions.pending,   (s) => { s.loading.suggestions = true; })
            .addCase(fetchItemSuggestions.fulfilled, (s, a) => { s.loading.suggestions = false; s.suggestions = a.payload; })
            .addCase(fetchItemSuggestions.rejected,  (s) => { s.loading.suggestions = false; s.suggestions = []; });

        builder
            .addCase(addIndentItem.pending,   (s) => { s.loading.addItem = true; s.errors.addItem = null; })
            .addCase(addIndentItem.fulfilled, (s, a) => { s.loading.addItem = false; s.items.push(a.payload); })
            .addCase(addIndentItem.rejected,  (s, a) => { s.loading.addItem = false; s.errors.addItem = a.payload; });

        builder
            .addCase(removeIndentItem.pending,   (s) => { s.loading.removeItem = true; s.errors.removeItem = null; })
            .addCase(removeIndentItem.fulfilled, (s, a) => { s.loading.removeItem = false; s.items = s.items.filter((i) => i.IndentListId !== a.payload); })
            .addCase(removeIndentItem.rejected,  (s, a) => { s.loading.removeItem = false; s.errors.removeItem = a.payload; });

        builder
            .addCase(submitIndentAmend.pending,   (s) => { s.loading.save = true; s.errors.save = null; s.saveResult = null; })
            .addCase(submitIndentAmend.fulfilled, (s, a) => { s.loading.save = false; s.saveResult = a.payload; })
            .addCase(submitIndentAmend.rejected,  (s, a) => { s.loading.save = false; s.errors.save = a.payload; });

        builder
            .addCase(closeIndent.pending,   (s) => { s.loading.close = true; s.errors.close = null; s.closeResult = null; })
            .addCase(closeIndent.fulfilled, (s, a) => { s.loading.close = false; s.closeResult = a.payload; })
            .addCase(closeIndent.rejected,  (s, a) => { s.loading.close = false; s.errors.close = a.payload; });
    },
});

export const {
    clearIndentList,
    clearAmendItems,
    clearItemSuggestions,
    clearQtyCheckResult,
    clearSaveResult,
    clearCloseResult,
    resetIndentAmend,
} = indentAmendSlice.actions;

// ── Selectors ────────────────────────────────────────────────────────────────
export const selectAmendCCList        = (s) => s.indentAmend.ccList;
export const selectAmendIndentList    = (s) => s.indentAmend.indentList;
export const selectAmendCategoryList  = (s) => s.indentAmend.categoryList;
export const selectQtyCheckResult     = (s) => s.indentAmend.qtyCheckResult;
export const selectAmendItems         = (s) => s.indentAmend.items;
export const selectAmendPOStatus      = (s) => s.indentAmend.poStatus;
export const selectItemSuggestions    = (s) => s.indentAmend.suggestions;
export const selectAmendSaveResult    = (s) => s.indentAmend.saveResult;
export const selectAmendCloseResult   = (s) => s.indentAmend.closeResult;
export const selectAmendLoading       = (s) => s.indentAmend.loading;
export const selectAmendErrors        = (s) => s.indentAmend.errors;

export default indentAmendSlice.reducer;
